import React, { Component } from 'react'
import { observer, inject } from 'mobx-react'
import Form from '../website/settings/Form'
import { present } from '../website/settings/Form/validators'
import Icon from '../../util/Icon'
import Help from '../../util/Help'
import HelpVideo from './HelpVideo'
import types from './types'
import flatten from 'lodash/flatten'

import IntegrationsStore from '../../../stores/integrations'

const events = {
  'Event::Message': 'Messages',
  'Event::SavedListing': 'Saved Listings',
  'Event::Registered': 'New Leads',
  'Event::ViewedListing': 'Viewed Listings',
  'Event::SavedSearch': 'Subscribed Searches',
  'Event::Comment': 'Comments',
  'Event::Reaction': 'Reactions',
  'Event::HiddenListing': 'Hidden Listings',
}

@inject('users', 'websites', 'auth')
@observer
export default class IntegrationsEdit extends Component {
  integrations = new IntegrationsStore()

  state = {
    level: 'Team',
    valid: null,
    validating: false,
  }

  componentDidMount() {
    this.props.users.load()
    this.props.websites.load()
    this.integrations.load(this.props.params.id).then(() => {
      this.setState({ level: this.integration.owner_type })
    })
    window.addEventListener("message", this.recievePostMessage)
  }

  componentWillUnmount() {
    window.removeEventListener("message", this.recievePostMessage)
  }

  recievePostMessage = event => {
    const { data } = event
    if (data.type === 'SIDX_INTEGRATION_TOKEN') {
      const { token } = data.payload
      this.apiKeyInput.value = token
      this.oauthWindow && this.oauthWindow.close()
      this.setState({ validating: true })
      this.integrations.verify(this.type, token).then(({ valid }) => {
        this.setState({ valid, validating: false })
      })
    }
  }

  handleOpenOAuth = event => {
    event.preventDefault()
    const x = (window.innerWidth / 2) + (window.screenLeft - 200)
    const y = (window.innerHeight / 2) + (window.screenTop - 200)
    this.oauthWindow = window.open(this.typeOAuth, 'sidx-integration-oauth', `width=400,height=400,left=${x},top=${y}`)
  }

  get integration() {
    return this.integrations.findById(this.props.params.id)
  }

  get typeName() {
    return types[this.integration.type].name
  }

  get typeImage() {
    return types[this.integration.type].image
  }

  get typeOAuth() {
    return types[this.integration.type].oauth
  }

  get tokenType() {
    return types[this.integration.type].tokenType
  }

  get typeVideo() {
    return types[this.integration.type].video
  }

  getNameLabel() {
    return (
      <span>
        <img src={`/images/integrations/${this.typeImage}`} />
        {' '}
        Editing:
        {' '}
        {this.integration.name}
      </span>
    )
  }

  getValidationBadge() {
    const { valid, validating } = this.state
    if (this.apiKeyInput && !this.apiKeyInput.value) {
      return null
    }
    if (valid === null && !validating) {
      return null
    }

    if (validating) {
      return (
        <span className="api-key validating">
          <Icon icon="spinner" className="fa-spin" /> Checking
        </span>
      )
    } else if (valid) {
      return (
        <span className="api-key valid">
          <Icon icon="check" /> Valid
        </span>
      )
    } else {
      return (
        <span className="api-key invalid">
          <Icon icon="times" /> Invalid
        </span>
      )
    }
  }

  getOauthSigninLink() {
    if (this.typeOAuth) {
      return (
        <button onClick={this.handleOpenOAuth}>Generate {this.tokenType}</button>
      )
    }
  }

  handleApiKey = event => {
    const api_key = event.target.value
    if (!api_key) {
      this.setState({ valid: true, validating: false })
      return
    }
    this.setState({ validating: true })
    this.integrations.verify(this.integration.type, api_key).then(({ valid }) => {
      this.setState({ valid, validating: false })
    })
  }

  handleLevel = event => {
    this.setState({ level: event.target.value })
  }

  handleData = data => {
    if (typeof data.hooks === 'string') {
      data.hooks = [data.hooks]
    }
    return this.integrations.update({ ...this.integration, ...data })
  }

  handleDelete = event => {
    event.preventDefault()
    const confirmed = window.confirm('Are you sure you want to permanently delete this integraiton?\nThis cannot be undone. To temporarily turn off, use pause.\n\nClick OK to permanently delete.')
    if (confirmed) {
      this.setState({ deleting:true })
      this.integrations.delete(this.integration.id).then(() => {
        this.props.router.push('/integrations')
      })
    }
  }

  handlePause = event => {
    event.preventDefault()
    const paused = !this.integration.paused
    this.setState({ pausing: true })
    this.integrations.update({ ...this.integration, paused }).then(() => {
      this.setState({ pausing: false })
    }).catch(error => {
      let problems
      const errors = error.response && error.response.data && error.response.data.errors
      if (errors) {
        const fields = Object.keys(errors)
        problems = flatten(fields.map(field => errors[field].map(msg => field === 'base' ? msg : `${field} ${msg}`)))
      } else {
        problems = [`${error}`]
      }
      this.setState({ pausing: false, problems })
    })
  }

  getProblems() {
    const { problems } = this.state
    if (problems && problems.length) {
      return (
        <ul className="error">
          {problems.map((msg, i) => <li key={`${msg}-${i}`}>{msg}</li>)}
        </ul>
      )
    }
  }

  getPauseButton() {
    const { pausing } = this.state
    let content
    if (pausing) {
      content = <Icon icon="spinner" className="fa-spin" />
    } else {
      content = this.integration.paused ? 'Unpause' : 'Pause'
    }
    return (
      <button className="pause-btn caution" onClick={!pausing && this.handlePause}>{content}</button>
    )
  }
 
  getOwnerInput() {
    switch (this.state.level) {
      case 'User': return (
        <div className="form-section">
          <p className="info">This will trigger on events only when the associated lead is assigned to the selected user.</p>
          <label htmlFor="owner_id">Select A User</label>
          <select name="owner_id" id="owner_id" defaultValue={this.integration.owner_id}>
            {this.props.users.map(user => (
              <option key={user.id} value={user.id}>{user.name}</option>
            ))}
          </select>
        </div>
      )
      case 'Website': return (
        <div className="form-section">
          <p className="info">This will trigger on events only for the selected website.</p>
          <label htmlFor="owner_id">Select A Website</label>
          <select name="owner_id" id="owner_id" defaultValue={this.integration.owner_id}>
            {Object.values(this.props.websites.websites).map(website => (
              <option key={website.id} value={website.id}>{website.name}</option>
            ))}
          </select>
        </div>
      )
      case 'Team': return (
        <div className="form-section">
          <p className="info">This will trigger on events for the entire account for all users and websites.</p>
          <input type="hidden" name="owner_id" value={this.props.users.records[0].team_id} />
        </div>
      )
    }
  }

  render() {
    if (!this.props.users.loaded || !this.props.websites.loaded || !this.integrations.loaded || this.state.deleting) {
      return <div className="loading">Loading...</div>
    }
    const validations = [
      { field: 'hooks', validate: present, message: 'Please Select At Least One Event' },
      { field: 'name', validate: present, message: 'Please Enter a Name' },
    ]
    return (
      <div className="container centered padded integration">
        <Form className="full" onData={this.handleData} onChange={this.handleChange} validations={validations}>
          <input type="hidden" name="type" value={this.integration.type} />

          {this.getProblems()}
  
          <div className="panel padded">
            <h2 className="title">
              <span>{this.getNameLabel()}</span>
              {this.getPauseButton()}
              <button className="danger" onClick={this.handleDelete}>Delete</button>
            </h2>

            <HelpVideo url={this.typeVideo} />

            <div className="form-section">
              <label htmlFor="api_key">{this.tokenType} <Help>We need to get the applicable {this.typeName} {this.tokenType} to wire up this integration.</Help> {this.getValidationBadge()}</label>
              <input ref={el => this.apiKeyInput = el} name="api_key" id="api_key" placeholder={`Enter your ${this.tokenType}`} onBlur={this.handleApiKey} defaultValue={this.integration.api_key} />
              {this.getOauthSigninLink()}
            </div>

            <div className="form-section">
              <label htmlFor="name">Name This Integration <Help>Pick a descriptive unique name for this integration to identify it.</Help></label>
              <input name="name" id="name" placeholder="Enter a Name" defaultValue={this.integration.name} />
            </div>

            <div style={{ borderTop: '1px solid #eee', margin: '10px 0' }} />

            <div className="form-section">
              <label htmlFor="owner_type">Select An Integration Level <Help>You can select an individual website or user to use this integration for, or the whole account.</Help></label>
              <select name="owner_type" id="owner_type" value={this.state.level} onChange={this.handleLevel}>
                <option value="Team">Account</option>
                <option value="Website">Website</option>
                <option value="User">User</option>
              </select>
            </div>

            {this.getOwnerInput()}

            <div style={{ borderTop: '1px solid #eee', margin: '10px 0' }} />

            <div className="form-section">
              <label>Select One Or More Events <Help>This integration will only be triggered for the events you select here.</Help></label>
              <div className="events-list">
                {this.integrations.available.find(int => int.type === this.integration.type).events.map(event => (
                  <div key={event} className="event-type">
                    <input name="hooks" type="checkbox" id={`event_${event}`} value={event} defaultChecked={this.integration.hooks.includes(event)} />
                    <label htmlFor={`event_${event}`}>{events[event]}</label>
                  </div>
                ))}
              </div>
            </div>

          </div>
          <button type="submit" className="big shadowed" disabled={!this.state.valid}>Save</button>

        </Form>
      </div>
    )
  }
}


// WEBPACK FOOTER //
// client/components/sections/Integrations/Edit.js